import React, { useState } from 'react';
import { Phone, Mail, Facebook, Instagram, Youtube, Linkedin, Send, MapPin, CheckCircle } from 'lucide-react';
import { contactData } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      toast.success('Message sent successfully!', {
        description: "We'll get back to you as soon as possible."
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ firstName: '', lastName: '', email: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: Facebook, href: contactData.socialMedia.facebook, label: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: Instagram, href: contactData.socialMedia.instagram, label: 'Instagram', color: 'hover:bg-pink-600' },
    { icon: Youtube, href: contactData.socialMedia.youtube, label: 'YouTube', color: 'hover:bg-red-600' },
    { icon: Linkedin, href: contactData.socialMedia.linkedin, label: 'LinkedIn', color: 'hover:bg-blue-700' }
  ];

  return (
    <section id="contact" className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-full">
          <img
            src={contactData.backgroundImage}
            alt="Washington DC"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="absolute top-20 left-10 w-64 h-64 bg-teal-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-200/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Connect With Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            {contactData.title}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {contactData.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100 mb-8">
              <h3 className="text-2xl font-bold text-slate-800 mb-8">Contact Information</h3>

              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-center gap-4 group">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center group-hover:bg-teal-200 transition-colors duration-300">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Phone</p>
                    <a
                      href={`tel:${contactData.phone}`}
                      className="text-lg font-semibold text-slate-800 hover:text-teal-600 transition-colors duration-300"
                    >
                      {contactData.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center gap-4 group">
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-300">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Email</p>
                    <a
                      href={`mailto:${contactData.email}`}
                      className="text-lg font-semibold text-slate-800 hover:text-emerald-600 transition-colors duration-300"
                    >
                      {contactData.email}
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4 group">
                  <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center group-hover:bg-amber-200 transition-colors duration-300 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Mailing Address</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {contactData.address}
                    </p>
                    <p className="text-slate-600">
                      {contactData.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100">
              <h3 className="text-xl font-bold text-slate-800 mb-6">Follow Us</h3>
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-600 ${social.color} hover:text-white transition-all duration-300 hover:scale-110`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-6 h-6" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100">
            <h3 className="text-2xl font-bold text-slate-800 mb-8">Send a Message</h3>

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-teal-600" />
                </div>
                <h4 className="text-xl font-bold text-slate-800 mb-2">Thanks for reaching out!</h4>
                <p className="text-slate-600 text-center">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="text-slate-700 mb-2 block">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                      placeholder="John"
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-slate-700 mb-2 block">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                      placeholder="Doe"
                      data-testid="contact-last-name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-700 mb-2 block">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 mb-2 block">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500 resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 group"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send Message
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
